import { Injectable } from '@angular/core';

/**
 * Utility service for building and normalizing URLs in SSR and browser contexts.
 * Ensures no double slashes or duplicate locale segments.
 */
@Injectable({ providedIn: 'root' })
export class UrlUtility {
  
  /**
   * Normalizes a URL by:
   * 1. Removing duplicate slashes (except after protocol)
   * 2. Removing duplicate locale segments like /en/en/ or /ar/ar/
   * 3. Ensuring proper trailing slash behavior
   * 
   * @param url The URL to normalize
   * @returns Normalized URL
   */
  public normalize(url: string): string {
    if (!url) return '';

    // Step 1: Split protocol from path
    const protocolMatch = url.match(/^(https?:\/\/)/);
    const protocol = protocolMatch ? protocolMatch[1] : '';
    let path = protocolMatch ? url.substring(protocol.length) : url;

    // Step 2: Remove duplicate slashes in path
    path = path.replace(/\/{2,}/g, '/');

    // Step 3: Remove duplicate locale segments (e.g., /en/en/ → /en/ or /ar/ar/ → /ar/)
    path = path.replace(/\/(en|ar)\/(en|ar)\//g, '/$1/');
    path = path.replace(/\/(en|ar)\/(en|ar)$/g, '/$1');

    // Step 4: Reconstruct the URL
    const normalized = protocol + path;

    return normalized;
  }

  /**
   * Builds a full URL from base, locale, and path segments.
   * Automatically normalizes the result.
   * 
   * @param baseUrl Base URL (e.g., 'https://card4games.com' or 'https://card4games.com/')
   * @param locale Language code ('en' or 'ar')
   * @param path Relative path (e.g., 'categories/gaming' or '/categories/gaming')
   * @returns Normalized full URL
   * 
   * @example
   * buildUrl('https://card4games.com', 'en', 'categories/gaming')
   * // Returns: 'https://card4games.com/en/categories/gaming'
   * 
   * buildUrl('https://card4games.com/en/', 'en', '/categories/gaming')
   * // Returns: 'https://card4games.com/en/categories/gaming'
   */
  public buildUrl(baseUrl: string, locale?: string, path?: string): string {
    if (!baseUrl) return '';

    // Remove trailing slash from baseUrl
    let base = baseUrl.replace(/\/+$/, '');

    // Remove any existing locale from base (e.g., 'https://card4games.com/en' → 'https://card4games.com')
    base = base.replace(/\/(en|ar)$/i, '');

    // Build segments
    const segments: string[] = [base];

    if (locale) {
      segments.push(locale);
    }

    if (path) {
      // Remove leading slash from path
      const cleanPath = path.replace(/^\/+/, '');
      if (cleanPath) {
        segments.push(cleanPath);
      }
    }

    // Join with single slashes
    const url = segments.join('/');

    // Normalize to remove any accidental duplicates
    return this.normalize(url);
  }

  /**
   * Extracts the locale (language) from a URL path.
   * 
   * @param url Full URL or path
   * @returns 'en', 'ar', or 'en' (default)
   * 
   * @example
   * extractLocale('https://card4games.com/ar/categories') // Returns: 'ar'
   * extractLocale('/en/blogs') // Returns: 'en'
   * extractLocale('/categories') // Returns: 'en' (default)
   */
  public extractLocale(url: string): 'en' | 'ar' {
    const match = url.match(/\/(en|ar)(?:\/|$)/i);
    return match ? (match[1].toLowerCase() as 'en' | 'ar') : 'en';
  }

  /**
   * Validates that a URL doesn't contain common errors:
   * - Double slashes (except after protocol)
   * - Duplicate locale segments
   * 
   * @param url URL to validate
   * @returns true if valid, false otherwise
   */
  public validate(url: string): boolean {
    if (!url) return false;

    // Check for double slashes (except protocol)
    const withoutProtocol = url.replace(/^https?:\/\//, '');
    if (withoutProtocol.includes('//')) {
      console.error(`❌ URL validation failed: Double slashes found in "${url}"`);
      return false;
    }

    // Check for duplicate locale segments
    if (/\/(en|ar)\/(en|ar)/.test(url)) {
      console.error(`❌ URL validation failed: Duplicate locale segments in "${url}"`);
      return false;
    }

    return true;
  }

  /**
   * Logs URL validation to console (useful for debugging SSR URLs)
   * Only logs in development or when explicitly enabled.
   */
  public logValidation(url: string, context: string): void {
    const isValid = this.validate(url);
    if (!isValid) {
      console.warn(`⚠️ [${context}] Invalid URL generated:`, url);
    }
  }
}
